import { ApiError, post } from '@/api/client';
import { destinationPath, forgetDestination } from '@/auth/destination';
import { type SessionUser, useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { track } from '@/marketing/analytics';
import { commitGeneration } from '@/shell/context';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { KontoBox, failureText, hint } from './shared';

/**
 * /logg-inn/fortsett?token=…: back from «Logg inn med Google/Microsoft/Apple» with an existing account
 * (owner 2026-10-06). The one-time link from the server becomes the same session a password login
 * gives (POST /account/oauth/finish), then the app opens; nothing to press.
 */
export default function Fortsett() {
  const nav = useNavigate();
  const { search } = useLocation();
  const setSession = useAuth((s) => s.setSession);
  const [err, setErr] = useState<string | null>(null);
  const once = useRef(false); // React's double mount in development must not spend the link twice

  useEffect(() => {
    if (once.current) return;
    once.current = true;
    const q = new URLSearchParams(search);
    const token = q.get('token');
    const via = q.get('via') ?? 'provider'; // google, microsoft or apple, as the server's redirect names it
    post<{ token: string; user: SessionUser; scope?: string }>('/account/oauth/finish', { token })
      .then((r) => {
        track('signin_succeeded', { method: via });
        setSession(r.token, r.user);
        commitGeneration(r.user.id, null, null); // a new context: nothing from a previous person is readable
        const to = r.scope === 'identity' ? destinationPath() : '/';
        forgetDestination();
        nav(to, { replace: true });
      })
      .catch((ex) => {
        track('signin_failed', {
          method: via,
          reason: ex instanceof ApiError ? (ex.code ?? 'link') : 'no_answer',
        });
        setErr(failureText(ex));
      });
  }, [search, nav, setSession]);

  return (
    <KontoBox title={err ? T('Innloggingen gikk ikke') : T('Logger inn …')}>
      {err && (
        <div className="opt" style={{ display: 'grid', gap: 10 }}>
          <div className="al dgr">{err}</div>
          <Link to="/login" style={hint}>
            {T('Til innlogging')}
          </Link>
        </div>
      )}
    </KontoBox>
  );
}
